import React, { useEffect } from 'react';
import styled from 'styled-components';
import { FiPlus, FiTrash2 } from 'react-icons/fi';

const EventHighlightsForm = ({ highlights, onChange }) => {
  
  useEffect(() => {
    console.log('EventHighlightsForm received highlights:', highlights);
  }, [highlights]);
  
  // Handle checkbox toggle
  const handleEnabledToggle = (checked) => {
    onChange('highlights.enabled', checked);
  };

  // Handle title/subtitle changes
  const handleFieldChange = (field, value) => {
    onChange(`highlights.${field}`, value);
  };

  // Handle card updates
  const handleCardChange = (index, field, value) => {
    console.log(`✨ Updating highlights card ${index}, field: ${field}, value:`, value);
    const updatedCards = [...(highlights.cards || [])];
    updatedCards[index] = {
      ...updatedCards[index],
      [field]: value
    };
    console.log('✨ Updated cards array:', updatedCards);
    onChange('highlights.cards', updatedCards);
  };

  // Add new card
  const addCard = () => {
    if ((highlights.cards || []).length >= 6) {
      alert('Maximum 6 highlight cards allowed');
      return;
    }
    const updatedCards = [...(highlights.cards || []), { icon: '✨', heading: '', description: '' }];
    console.log('Adding card. Current cards:', highlights.cards);
    console.log('Updated cards:', updatedCards);
    onChange('highlights.cards', updatedCards);
  };

  // Remove card
  const removeCard = (index) => {
    const updatedCards = (highlights.cards || []).filter((_, i) => i !== index);
    onChange('highlights.cards', updatedCards);
  };

  return (
    <HighlightsContainer>
      <SectionHeader>
        <SectionTitle>Event Highlights Section (Optional)</SectionTitle>
        <EnableCheckbox>
          <input
            type="checkbox"
            checked={highlights.enabled || false}
            onChange={(e) => handleEnabledToggle(e.target.checked)}
            id="highlightsEnabled"
          />
          <label htmlFor="highlightsEnabled">
            Enable Highlights Section
          </label>
        </EnableCheckbox>
      </SectionHeader>

      <FormDescription>
        Add up to 6 highlight cards with custom emoji/icon, heading, and description. Cards display in a responsive grid.
      </FormDescription>

      {/* Header Section */}
      <SubSection>
        <SubSectionTitle>Section Header</SubSectionTitle>
        
        <FormGroup>
          <Label>Title</Label>
          <Input
            type="text"
            placeholder="Event Highlights"
            value={highlights.title || ''}
            onChange={(e) => handleFieldChange('title', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Subtitle</Label>
          <Input
            type="text"
            placeholder="Creating Moments That Last Forever"
            value={highlights.subtitle || ''}
            onChange={(e) => handleFieldChange('subtitle', e.target.value)}
          />
        </FormGroup>
      </SubSection>

      {/* Cards Section */}
      <SubSection>
        <SubSectionTitle>
          Highlight Cards ({(highlights.cards || []).length}/6)
          {(highlights.cards || []).length < 6 && (
            <AddCardBtn onClick={addCard}>
              <FiPlus size={14} /> Add Card
            </AddCardBtn>
          )}
        </SubSectionTitle>

        {(highlights.cards || []).map((card, index) => (
          <CardItem key={index}>
            <CardHeader>
              <CardNumber>Card {index + 1}</CardNumber>
              <RemoveCardBtn onClick={() => removeCard(index)}>
                <FiTrash2 size={14} /> Remove
              </RemoveCardBtn>
            </CardHeader>
            
            <CardGrid>
              <FormGroup>
                <Label>Icon/Emoji</Label>
                <IconInput
                  type="text"
                  placeholder="✨"
                  value={card.icon || ''}
                  onChange={(e) => handleCardChange(index, 'icon', e.target.value)}
                  maxLength="10"
                />
                <Hint>Use emoji or SVG code. Preview: <IconPreview>{card.icon || '✨'}</IconPreview></Hint>
              </FormGroup>

              <FormGroup style={{ gridColumn: '2' }}>
                <Label>Heading *</Label>
                <Input
                  type="text"
                  placeholder="e.g., Expert Planning"
                  value={card.heading || ''}
                  onChange={(e) => handleCardChange(index, 'heading', e.target.value)}
                />
              </FormGroup>
            </CardGrid>

            <FormGroup>
              <Label>Description *</Label>
              <Textarea
                placeholder="Describe this highlight..."
                value={card.description || ''}
                onChange={(e) => handleCardChange(index, 'description', e.target.value)}
                rows={3}
              />
            </FormGroup>
          </CardItem>
        ))}

        {(!highlights.cards || highlights.cards.length === 0) && (
          <EmptyState onClick={addCard}>
            <FiPlus size={32} color="#ccc" />
            <p>Click to add your first highlight card</p>
          </EmptyState>
        )}
      </SubSection>

      {/* Preview */}
      {highlights.cards && highlights.cards.length > 0 && (
        <PreviewSection>
          <PreviewTitle>Preview</PreviewTitle>
          <PreviewGrid>
            {highlights.cards.map((card, index) => (
              <PreviewCard key={index}>
                <PreviewIcon>{card.icon || '✨'}</PreviewIcon>
                <PreviewHeading>{card.heading || 'Heading'}</PreviewHeading>
                <PreviewDescription>{card.description || 'Description'}</PreviewDescription>
              </PreviewCard>
            ))}
          </PreviewGrid>
        </PreviewSection>
      )}
    </HighlightsContainer>
  );
};

// Styled Components
const HighlightsContainer = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 24px;
  margin-top: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const EnableCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  label {
    font-size: 14px;
    font-weight: 500;
    color: #555;
    cursor: pointer;
  }
`;

const FormDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.5;
`;

const SubSection = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
`;

const SubSectionTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const IconInput = styled(Input)`
  text-align: center;
  font-size: 24px;
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const Hint = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconPreview = styled.span`
  font-size: 20px;
  padding: 4px 8px;
  background: #f0f0f0;
  border-radius: 4px;
`;

const AddCardBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #45a049;
  }
`;

const CardItem = styled.div`
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 12px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const CardNumber = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #555;
`;

const RemoveCardBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #ff6b6b;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #ff5252;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 12px;
  margin-bottom: 12px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #999;
  cursor: pointer;
  border: 2px dashed #ddd;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    border-color: #4CAF50;
    color: #4CAF50;
  }

  p {
    margin-top: 12px;
    font-size: 14px;
  }
`;

const PreviewSection = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
`;

const PreviewTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const PreviewCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 20px 15px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(201, 162, 39, 0.1);
`;

const PreviewIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const PreviewHeading = styled.h5`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;
`;

const PreviewDescription = styled.p`
  font-size: 12px;
  line-height: 1.5;
  color: #666;
`;

export default EventHighlightsForm;
